import axiosInstance from './axiosConfig';
import type { ImageTag } from './imageTagService';
import { buildStyleBrowsingComparator } from '../../utils/styleBrowsingOrder';

// API base URL for fixing image URLs in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');

/**
 * Fix image URL for local development
 * Converts http://localhost/storage/... to http://localhost:8003/storage/...
 */
const fixImageUrl = (url: string): string => {
  if (!url) return url;
  // Only fix localhost URLs without port
  if (url.startsWith('http://localhost/storage/')) {
    return url.replace('http://localhost/storage/', `${STORAGE_BASE_URL}/storage/`);
  }
  return url;
};

/**
 * Fix image URLs in GeneratedImage object
 */
const fixGeneratedImageUrls = (image: GeneratedImage): GeneratedImage => {
  return {
    ...image,
    url: fixImageUrl(image.url),
  };
};

export type GenerationSource = 'banner_ads' | 'image_generation' | 'lp_overlay' | 'carousel_ads';

/**
 * Optional generation options shared by generateImage / generateImageWithReferences.
 * All fields are optional so existing callers are unaffected; when omitted the
 * backend falls back to its defaults (gpt-image-2 engine, banner_ads source).
 */
export interface GenerateImageOptions {
  // UI からのモデル選択は廃止済み。常に constants/imageModel の値を渡す。
  model?: string;            // 'gpt-image-2' | 'nanobanana2'
  aspectRatio?: string;      // e.g. '1:1', '4:5'
  generationSource?: GenerationSource;
  textless?: boolean;
}

/**
 * Convert GenerateImageOptions into the snake_case request payload the API
 * expects, omitting undefined keys so we never override backend defaults.
 */
const buildGenerateOptionsPayload = (options?: GenerateImageOptions): Record<string, unknown> => {
  if (!options) return {};
  const payload: Record<string, unknown> = {};
  if (options.model !== undefined) payload.model = options.model;
  if (options.aspectRatio !== undefined) payload.aspect_ratio = options.aspectRatio;
  if (options.generationSource !== undefined) payload.generation_source = options.generationSource;
  if (options.textless !== undefined) payload.textless = options.textless;
  return payload;
};
export type ImageHistorySort = 'newest' | 'product_name_asc' | 'product_name_desc' | 'product_only';

export interface ImageHistoryOptions {
  favorite?: boolean;
  sort?: ImageHistorySort;
  /** 全部が付いたバナーだけに絞る（AND） */
  tagIds?: number[];
  productId?: number;
}

function imageHistoryParams(page: number, source: GenerationSource | undefined, options?: ImageHistoryOptions): string {
  const params = new URLSearchParams({ page: String(page) });
  if (source) params.append('source', source);
  if (options?.favorite) params.append('favorite', '1');
  if (options?.sort) params.append('sort', options.sort);
  if (options?.tagIds?.length) params.append('tags', options.tagIds.join(','));
  if (options?.productId !== undefined) params.append('product_id', String(options.productId));
  return params.toString();
}

export interface TextElement {
  id: number;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: 'large' | 'medium' | 'small';
  role: 'headline' | 'subheadline' | 'body' | 'cta' | 'price' | 'label';
}

/**
 * 訴求（type/appeal_axis/name）。DCM実データ連携後の新intent構造でのみ登場する（後述）。
 */
export interface CreativeAnalysisIntentAppeal {
  type?: string;
  appeal_axis?: string;
  name?: string;
}

export interface CreativeAnalysis {
  overall_score: number;
  scores: {
    impression: number;
    visibility: number;
    comprehension: number;
    copy: number;
    action: number;
  };
  feedback: string;
  intent: {
    // セグメント名（新形式ではファネル段階も含む結合文字列。例:「〜責任者 × 顕在層」）
    segment: string;
    // 旧形式（DCM連携前に生成されたバナー）: appeal は単純な文字列、summary が存在する。
    // 新形式（DCM連携後）: appeal はオブジェクト（type/appeal_axis/name）、summary は存在しない。
    // typeof intent.appeal で判定する。
    summary?: string;
    appeal: string | CreativeAnalysisIntentAppeal;
    insight?: string;
    creative_intent?: string;
  };
}

export interface GeneratedImage {
  id: number;
  prompt: string;
  url: string;
  // 履歴グリッド表示用の縮小サムネ（P1-1）。無い行は null → 原寸 url にフォールバック。
  thumbnail_url?: string | null;
  s3_key?: string;
  description?: string;
  file_size: number;
  created_at: string;
  is_global_sample?: boolean;
  input_mode?: 'freetext' | 'product' | 'edit' | 'magic_resize';
  generation_source?: GenerationSource;
  source_image_id?: number | null;
  style_id?: number | null;
  style_name?: string | null;
  style_rating?: StyleRating | null;
  is_favorited?: boolean;
  // 自分の組織で付けたタグだけが入る
  tags?: ImageTag[];
  product_id?: number | null;
  product_name?: string | null;
  ad_score?: number | null;
  job_id?: number | string | null;
  creative_analysis?: CreativeAnalysis | null;
  analyzed_at?: string | null;
  metadata?: {
    carousel_set_id?: string;
    slide_index?: number;
    slide_count?: number;
    slide_role?: string;
  } | null;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

export type StyleRating = 'good' | 'bad';

export interface GeneratedImageStyleFeedbackResponse {
  success: boolean;
  data: {
    generated_image_id: number;
    image_style_id: number;
    rating: StyleRating | null;
    reason?: string | null;
    ratings_summary?: {
      good: number;
      bad: number;
    };
  };
}

export interface ImageVersionsResponse {
  success: boolean;
  data: {
    current_id: number;
    current_index: number;
    versions: GeneratedImage[];
  };
}

export type SamplePromptType = 'material' | 'banner';

export interface SamplePrompt {
  id: number;
  name: string;
  prompt_text: string;
  type?: SamplePromptType;
}

interface SamplePromptsResponse {
  success: boolean;
  data: SamplePrompt[];
}

export interface GenerateImageRequest {
  prompt: string;
}

export interface EditImageRequest {
  prompt: string;
  image_data: string; // Base64 encoded image
}

export interface ReferenceImage {
  image_data: string; // Base64 encoded image
  category: 'brand_logo' | 'product_image' | 'material';
}

export interface ReferenceImageUrl {
  url: string;
  category: 'brand_logo' | 'product_image' | 'material';
}

export interface GenerateWithReferencesRequest {
  prompt: string;
  reference_images: ReferenceImage[];
}

export interface ImageGenerationResponse {
  success: boolean;
  data: GeneratedImage;
  message?: string;
  error?: string;
}

export interface ImageHistoryResponse {
  success: boolean;
  data: {
    data: GeneratedImage[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface ToggleGlobalSampleResponse {
  success: boolean;
  data?: {
    id: number;
    is_global_sample: boolean;
  };
  message?: string;
  error?: string;
}

export interface TemporaryImage {
  temp_id: string;
  url: string;      // Presigned URL for display
  s3_key: string;
  s3_url: string;   // Base S3 URL for database storage
  file_size: number;
  description?: string;
  ad_score?: number | null;
}

export interface MultipleImageGenerationResponse {
  success: boolean;
  data: {
    images: TemporaryImage[];
    prompt: string;
  };
  errors?: string[];
  message?: string;
  error?: string;
}

// Async image generation job types
export type JobStatus = 'pending' | 'generating_prompt' | 'processing' | 'completed' | 'failed';

export interface ImageGenerationJob {
  id: string;
  status: JobStatus;
  prompt: string;
  count: number;
  progress: number;
  completed_count: number;
  error_message?: string;
  input_mode?: 'freetext' | 'product' | 'edit' | 'magic_resize';
  target_width?: number;
  target_height?: number;
  reference_images?: Array<{
    s3_key?: string;
    image_data?: string;
    category?: string;
  }>;
  has_reference_images?: boolean;
  avg_ad_score?: number | null;
  model?: string; // 'gemini' | 'seedream' | 'gpt-image' | 'gpt-image-2' | 'nanobanana2'
  metadata?: {
    carousel_set_id?: string;
    slide_index?: number;
    slide_count?: number;
    slide_role?: string;
    carousel_structure_name?: string;
    visual_style_name?: string;
    operation?: 'magic_resize';
    source_image_id?: number;
    target_sizes?: Array<{ width: number; height: number; prompt?: string | null }>;
  } | null;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

export interface JobImage {
  id: number;
  url: string;
  s3_key: string;
  file_size: number;
  source_image_id?: number | null;
  ad_score?: number | null;
  predicted_observations?: number | null;
  predicted_plays?: number | null;
  created_at: string;
}

export interface AsyncGenerateRequest {
  prompt?: string;
  count: number;
  reference_images?: Array<{
    type: 'brand_logo' | 'product_image' | 'material';
    s3_key?: string;
    image_data?: string;
  }>;
  target_width?: number;
  target_height?: number;
  input_mode?: 'freetext' | 'product';
  model?: string; // 'gemini' | 'seedream' | 'gpt-image' | 'gpt-image-2' | 'nanobanana2'
  // Product mode: prompt generated server-side
  product_id?: number;
  style_id?: number;
  prompt_generation_params?: Record<string, unknown>;
  generation_source?: GenerationSource;
  pro_mode?: boolean;
  metadata?: Record<string, unknown>;
}

export interface AsyncGenerateResponse {
  success: boolean;
  job: ImageGenerationJob;
  message?: string;
}

/**
 * 同時実行できるジョブの枠。generation_source で絞り込んでも、サーバ側のガードは
 * source を問わずユーザー全体のジョブを数えるため、active_jobs は常に全 source の合計。
 */
export interface ConcurrencyLimits {
  max_concurrent_jobs: number;
  active_jobs: number;
}

export interface JobsListResponse {
  jobs: ImageGenerationJob[];
  limits?: ConcurrencyLimits;
}

export interface JobDetailResponse {
  job: ImageGenerationJob;
  images: JobImage[];
  max_concurrent_jobs?: number;
}

export interface JobCancelResponse {
  success: boolean;
  message: string;
}

// ========================================
// Prompt Generation Types
// ========================================

/**
 * Structured sections for generated prompts
 * 7 sections following the design specification
 */
export interface PromptStructuredSections {
  purpose: string;        // Purpose
  tone: string;           // Tone/World view
  color_design: string;   // Color/Design
  subjects: string;       // Subjects/Visual elements
  brand_expression: string; // Product/Brand expression
  text_copy: string;      // Text/Catchcopy
  layout: string;         // Font/Layout
}

/**
 * Reference image for prompt generation
 */
export interface PromptReferenceImage {
  category: 'brand_logo' | 'product_image' | 'material';
  description?: string;
}

/**
 * Request for generating image prompt
 * Includes format parameters for delivery platform (keisiki)
 */
export interface GeneratePromptRequest {
  product_id: number;
  style_id?: number;
  additional_context?: string;
  reference_images?: PromptReferenceImage[];
  target_segment?: string;
  appeal_axis?: string;
  include_person?: string;
  // Format parameters (from keisiki.md)
  format_name?: string;      // Delivery platform name (e.g., "Instagram Feed Ad")
  format_cta?: string;       // CTA presence ("yes" / "no")
  format_feature?: string;   // Platform-specific features/characteristics
  // Count parameter for generating multiple variations
  count?: number;            // Number of variations to generate (default: 1)
  // Unified prompt parameters (passed to GPT-5.2 for integrated prompt generation)
  style_prompt?: string;     // Style description/prompt
  aspect_ratio?: string;     // Aspect ratio (e.g., "1:1", "16:9", "9:16")
  textless_mode?: boolean;   // True to generate images without text
}

/**
 * Request for regenerating image prompt
 */
export interface RegeneratePromptRequest {
  product_id: number;
  style_id?: number;
  previous_prompt: string;
  feedback?: string;
  reference_images?: PromptReferenceImage[];
  target_segment?: string;
  appeal_axis?: string;
  include_person?: string;
  // Format parameters (from keisiki.md)
  format_name?: string;
  format_cta?: string;
  format_feature?: string;
  // Unified prompt parameters (passed to GPT-5.2 for integrated prompt generation)
  style_prompt?: string;     // Style description/prompt
  aspect_ratio?: string;     // Aspect ratio (e.g., "1:1", "16:9", "9:16")
  textless_mode?: boolean;   // True to generate images without text
}

/**
 * Prompt variation (for count > 1)
 */
export interface PromptVariation {
  prompt: string;
  estimated_char_count: number;
  variation_index: number;
}

/**
 * Generated prompt data
 */
export interface GeneratedPrompt {
  prompt?: string;  // Single prompt (when count = 1)
  variations?: PromptVariation[];  // Multiple prompts (when count > 1)
  count?: number;
  structured_sections?: PromptStructuredSections;
  estimated_char_count?: number;
  style_name?: string;
  product_name: string;
  target_segment?: string;
  appeal_axis?: string;
  include_person?: string;
}

/**
 * Prompt options for target segments, appeal axes, and person display
 */
export interface PromptOptions {
  target_segments: Record<string, string>;
  appeal_axes: Record<string, string>;
  person_display_options: Record<string, string>;
}

/**
 * Response for prompt options
 */
export interface PromptOptionsResponse {
  success: boolean;
  data?: PromptOptions;
  message?: string;
}

// ========================================
// Magic Resize Types
// ========================================

export interface MagicResizePreset {
  id: number;
  name: string;
  width: number;
  height: number;
  display_order: number;
}

export interface MagicResizeCategory {
  id: number;
  name: string;
  display_order: number;
  presets: MagicResizePreset[];
}

export interface MagicResizePresetsResponse {
  success: boolean;
  data: MagicResizeCategory[];
  message?: string;
}

export interface MagicResizeExecuteRequest {
  sizes: Array<{ width: number; height: number }>;
}

export interface MagicResizeExecuteResponse {
  success: boolean;
  message?: string;
  data?: {
    queued_count: number;
    job_id?: string;
    job?: ImageGenerationJob;
  };
}

/**
 * Response for prompt generation/regeneration
 */
export interface GeneratePromptResponse {
  success: boolean;
  data?: GeneratedPrompt;
  message?: string;
  error?: string;
}

// Platform type for image styles
export type PlatformType = 'instagram' | 'facebook' | 'youtube' | 'x' | 'tiktok' | 'line' | 'yahoo_banner' | 'google_banner' | 'smartnews' | 'website' | 'mailmag';

// Image Style Category for user-facing use
export interface ImageStyleCategoryUser {
  id: number;
  name: string;
  slug: string;
  description?: string;
  styles_count: number;
}

export interface ImageStyleCategoriesResponse {
  success: boolean;
  data: ImageStyleCategoryUser[];
}

// Image Style types
export interface ImageStyle {
  id: number;
  name: string;
  description: string;
  category: string;  // Dynamic category slug from database
  tags?: string[];
  subcategory_id?: number | null;
  subcategory_name?: string | null;
  subcategory_slug?: string | null;
  platforms?: PlatformType[];  // Platform array (e.g., ['instagram', 'facebook'])
  aspect_ratio: string;
  style_prompt: string;
  example_image_url?: string;
  is_active: boolean;
  is_favorited: boolean;
  my_rating?: 'good' | 'bad' | null;
  my_rating_reason?: string | null;
  ratings_summary?: {
    good: number;
    bad: number;
  };
  created_at: string;
  updated_at: string;
  // Format-specific fields
  cta?: string;       // CTA presence (e.g., "yes", "no")
  feature?: string;   // Platform-specific feature description
  // Material count constraints (used to trim reference images on the client)
  min_material_count?: number;
  max_material_count?: number | null;
  // Dominant tone of the style's background. null/undefined = not classified.
  background_tone?: 'light' | 'dark' | null;
}

// Raw API response types (from backend)
interface RawImageStyle {
  id: number;
  name: string;
  prompt_template: string;
  reference_image_url?: string;
  thumbnail_url?: string;
  description: string;
  tags?: string[];
  sort_order: number;
  is_favorited?: boolean;
  subcategory_id?: number | null;
  subcategory_name?: string | null;
  subcategory_slug?: string | null;
  my_rating?: 'good' | 'bad' | null;
  my_rating_reason?: string | null;
  ratings_summary?: {
    good: number;
    bad: number;
  };
  created_at?: string;
  cta?: string;
  feature?: string;
  platforms?: PlatformType[];
  min_material_count?: number;
  max_material_count?: number | null;
  background_tone?: 'light' | 'dark' | null;
}

interface GroupedStyles {
  category: string;
  styles: RawImageStyle[];
}

interface RawImageStylesResponse {
  success: boolean;
  data: {
    categories: string[];
    grouped_styles: GroupedStyles[];
    total_count: number;
  };
}

export interface ImageStylesResponse {
  success: boolean;
  data: ImageStyle[];
}

export interface ImageStyleResponse {
  success: boolean;
  data: ImageStyle;
}

export interface RecommendedStyle {
  id: number;
  name: string;
  thumbnail_url: string;
  category_id: number;
  prompt_template: string;
  min_material_count?: number;
  max_material_count?: number | null;
  popularity_score?: number;
  usage_count_30d?: number;
  favorite_count?: number;
  good_rating_count?: number;
  bad_rating_count?: number;
  recommendation_badge?: string | null;
}

export interface StyleRecommendationsResponse {
  success: boolean;
  data: {
    recommended: RecommendedStyle[];
    reason: string;
    category_slug: string | null;
  };
}

class ImageGenerationService {
  /**
   * Generate image from text prompt
   */
  async generateImage(prompt: string, options?: GenerateImageOptions): Promise<ImageGenerationResponse> {
    const response = await axiosInstance.post<ImageGenerationResponse>(
      '/images/generate',
      { prompt, ...buildGenerateOptionsPayload(options) }
    );
    // Fix image URL for local development
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedImageUrls(response.data.data);
    }
    return response.data;
  }

  /**
   * Extract text elements from an image for text editing
   */
  async extractTextElements(params: {
    imageData?: string;
    imageUrl?: string;
    imageWidth: number;
    imageHeight: number;
  }): Promise<{
    success: boolean;
    data?: { elements: TextElement[] };
    message?: string;
  }> {
    const response = await axiosInstance.post('/images/extract-text', {
      image_data: params.imageData,
      image_url: params.imageUrl,
      image_width: params.imageWidth,
      image_height: params.imageHeight,
    });
    return response.data;
  }

  /**
   * Edit image with text prompt
   */
  async editImage(
    prompt: string,
    imageData?: string,
    imageUrl?: string,
    editProvider: 'openai' | 'gpt5.5' | 'gemini' | 'nanobanana' = 'openai',
    maskImageData?: string
  ): Promise<ImageGenerationResponse> {
    const response = await axiosInstance.post<ImageGenerationResponse>(
      '/images/edit',
      {
        prompt,
        image_data: imageData,
        image_url: imageUrl,
        edit_provider: editProvider,
        mask_image_data: maskImageData,
      },
      { timeout: 300000 }
    );
    // Fix image URL for local development
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedImageUrls(response.data.data);
    }
    return response.data;
  }

  /**
   * Edit image asynchronously and poll until the generated image is ready.
   */
  async editImageAsync(
    prompt: string,
    imageData?: string,
    imageUrl?: string,
    imageModel?: string,
    generationSource?: GenerationSource,
    editProvider?: 'openai' | 'gpt5.5' | 'gemini' | 'nanobanana',
    sourceImageId?: number,
    maskImageData?: string,
    onProgress?: (job: ImageGenerationJob) => void,
    metadata?: Record<string, unknown>
  ): Promise<ImageGenerationResponse> {
    const startResponse = await axiosInstance.post<AsyncGenerateResponse>(
      '/images/edit-async',
      {
        prompt,
        image_data: imageData,
        image_url: imageUrl,
        image_model: imageModel,
        generation_source: generationSource,
        edit_provider: editProvider,
        source_image_id: sourceImageId,
        mask_image_data: maskImageData,
        metadata,
      },
      { timeout: 30000 }
    );

    let job = startResponse.data.job;
    onProgress?.(job);

    const deadline = Date.now() + 10 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      const detail = await this.getJobDetail(job.id);
      job = detail.job;
      onProgress?.(job);

      if (job.status === 'completed') {
        const image = detail.images[0];
        if (!image) {
          throw new Error('編集済み画像が見つかりません');
        }

        return {
          success: true,
          data: {
            id: image.id,
            prompt: job.prompt,
            url: fixImageUrl(image.url),
            s3_key: image.s3_key,
            file_size: image.file_size,
            created_at: image.created_at,
            input_mode: 'edit',
            generation_source: generationSource,
            source_image_id: image.source_image_id,
            ad_score: image.ad_score,
            job_id: job.id,
          },
        };
      }

      if (job.status === 'failed') {
        throw new Error(job.error_message || 'AI編集ジョブに失敗しました');
      }
    }

    throw new Error('AI編集ジョブがタイムアウトしました');
  }

  /**
   * Composite text and uploaded image overlays without AI regeneration
   */
  async compositeImage(params: {
    imageData?: string;
    imageUrl?: string;
    texts?: Array<{
      text: string;
      x: number;
      y: number;
      font_size: number;
      color: string;
      font_family: string;
      text_shadow?: boolean;
    }>;
    imageOverlays?: Array<{
      image_data: string;
      region: { x: number; y: number; width: number; height: number };
    }>;
    generationSource?: GenerationSource;
    sourceImageId?: number;
    metadata?: Record<string, unknown>;
  }): Promise<ImageGenerationResponse> {
    const response = await axiosInstance.post<ImageGenerationResponse>(
      '/images/composite',
      {
        image_data: params.imageData,
        image_url: params.imageUrl,
        texts: params.texts ?? [],
        image_overlays: params.imageOverlays ?? [],
        generation_source: params.generationSource ?? 'image_generation',
        source_image_id: params.sourceImageId,
        metadata: params.metadata,
      },
      { timeout: 120000 }
    );
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedImageUrls(response.data.data);
    }
    return response.data;
  }

  /**
   * Get user's image generation history
   */
  async getImageHistory(
    page: number = 1,
    source?: GenerationSource,
    options?: ImageHistoryOptions
  ): Promise<ImageHistoryResponse> {
    const response = await axiosInstance.get<ImageHistoryResponse>(
      `/images?${imageHistoryParams(page, source, options)}`
    );
    // Fix image URLs for local development
    if (response.data.success && response.data.data?.data) {
      response.data.data.data = response.data.data.data.map(fixGeneratedImageUrls);
    }
    return response.data;
  }

  async getImageVersions(imageId: number): Promise<ImageVersionsResponse> {
    const response = await axiosInstance.get<ImageVersionsResponse>(
      `/images/${imageId}/versions`
    );
    if (response.data.success && response.data.data?.versions) {
      response.data.data.versions = response.data.data.versions.map(fixGeneratedImageUrls);
    }
    return response.data;
  }

  /**
   * Get organization's image generation history
   * Returns all images generated by users in the same organization
   */
  async getOrganizationImageHistory(
    page: number = 1,
    source?: GenerationSource,
    options?: ImageHistoryOptions
  ): Promise<ImageHistoryResponse> {
    const response = await axiosInstance.get<ImageHistoryResponse>(
      `/images/organization?${imageHistoryParams(page, source, options)}`
    );
    // Fix image URLs for local development
    if (response.data.success && response.data.data?.data) {
      response.data.data.data = response.data.data.data.map(fixGeneratedImageUrls);
    }
    return response.data;
  }

  /** 詳細を開いたことを残す（スタイル採点用）。失敗しても画面には影響させない */
  async recordDetailOpened(imageId: number): Promise<void> {
    try {
      await axiosInstance.post(`/images/${imageId}/detail-opened`);
    } catch {
      // 採点用の記録なので握りつぶす
    }
  }

  async toggleImageFavorite(imageId: number): Promise<{ id: number; is_favorited: boolean }> {
    const response = await axiosInstance.post<{ success: boolean; data: { id: number; is_favorited: boolean } }>(
      `/images/${imageId}/favorite`
    );
    return response.data.data;
  }

  async rateGeneratedImageStyle(
    imageId: number,
    rating: StyleRating,
    options?: { reason?: string; context?: Record<string, unknown> }
  ): Promise<GeneratedImageStyleFeedbackResponse['data']> {
    const response = await axiosInstance.post<GeneratedImageStyleFeedbackResponse>(
      `/images/${imageId}/style-feedback`,
      { rating, ...options }
    );
    return response.data.data;
  }

  async deleteGeneratedImageStyleRating(imageId: number): Promise<GeneratedImageStyleFeedbackResponse['data']> {
    const response = await axiosInstance.delete<GeneratedImageStyleFeedbackResponse>(
      `/images/${imageId}/style-feedback`
    );
    return response.data.data;
  }

  /**
   * Delete generated image
   */
  async deleteImage(id: number): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/images/${id}`
    );
    return response.data;
  }

  /**
   * Convert image file to base64
   */
  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          // Remove data URL prefix (e.g., "data:image/png;base64,")
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert file to base64'));
        }
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Get image as base64 data (for use as reference image)
   * Uses backend API to avoid CORS issues
   */
  async getImageBase64(id: number): Promise<{ success: boolean; data?: { id: number; base64: string; url: string }; message?: string }> {
    const response = await axiosInstance.get<{ success: boolean; data?: { id: number; base64: string; url: string }; message?: string }>(
      `/images/${id}/base64`
    );
    return response.data;
  }

  /**
   * Convert image URL to base64
   */
  async urlToBase64(url: string): Promise<string> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          } else {
            reject(new Error('Failed to convert URL to base64'));
          }
        };
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error('Failed to fetch image from URL:', error);
      throw error;
    }
  }

  /**
   * Generate image with reference images from product library
   * Sends URLs to backend which will fetch and convert to base64
   */
  async generateImageWithReferences(
    prompt: string,
    referenceImages: Array<{
      url: string;
      category: 'brand_logo' | 'product_image' | 'material';
    }>,
    options?: GenerateImageOptions
  ): Promise<ImageGenerationResponse> {
    // Send URLs directly to backend - backend will handle fetching and base64 conversion
    const referenceImageUrls: ReferenceImageUrl[] = referenceImages.map((img) => ({
      url: img.url,
      category: img.category,
    }));

    const response = await axiosInstance.post<ImageGenerationResponse>(
      '/images/generate-with-urls',
      {
        prompt,
        reference_image_urls: referenceImageUrls,
        ...buildGenerateOptionsPayload(options),
      }
    );
    // Fix image URL for local development
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedImageUrls(response.data.data);
    }
    return response.data;
  }

  /**
   * Generate multiple images for selection
   */
  async generateMultipleImages(
    prompt: string,
    count: number = 3,
    referenceImages?: Array<{
      url: string;
      category: 'brand_logo' | 'product_image' | 'material';
    }>
  ): Promise<MultipleImageGenerationResponse> {
    const payload: any = { prompt, count };

    if (referenceImages && referenceImages.length > 0) {
      payload.reference_image_urls = referenceImages.map((img) => ({
        url: img.url,
        category: img.category,
      }));
    }

    const response = await axiosInstance.post<MultipleImageGenerationResponse>(
      '/images/generate-multiple',
      payload
    );

    // Fix image URLs for local development
    if (response.data.success && response.data.data?.images) {
      response.data.data.images = response.data.data.images.map((img) => ({
        ...img,
        url: fixImageUrl(img.url),
      }));
    }

    return response.data;
  }

  /**
   * Save selected image to database
   */
  async saveSelectedImage(
    prompt: string,
    s3Key: string,
    s3Url: string,
    fileSize: number,
    adScore?: number | null
  ): Promise<ImageGenerationResponse> {
    const response = await axiosInstance.post<ImageGenerationResponse>(
      '/images/save-selected',
      {
        prompt,
        s3_key: s3Key,
        s3_url: s3Url,
        file_size: fileSize,
        ...(adScore != null && { ad_score: adScore }),
      }
    );

    // Fix image URL for local development
    if (response.data.success && response.data.data) {
      response.data.data = fixGeneratedImageUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Delete temporary image (not selected)
   */
  async deleteTemporaryImage(s3Key: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      '/images/delete-temporary',
      { s3_key: s3Key }
    );
    return response.data;
  }

  // ========================================
  // Async Image Generation Methods
  // ========================================

  /**
   * Start async image generation
   */
  async generateAsync(
    prompt: string,
    count: number,
    referenceImages?: Array<{
      type: 'brand_logo' | 'product_image' | 'material';
      s3_key?: string;
      image_data?: string;
    }>,
    targetWidth?: number,
    targetHeight?: number,
    inputMode?: 'freetext' | 'product',
    generationParams?: {
      product_id: number;
      style_id?: number;
      prompt_generation_params: Record<string, unknown>;
    } | {
      style_id: number;
      product_id?: never;
      prompt_generation_params?: never;
    },
    generationSource?: GenerationSource,
    model?: string,
    proMode?: boolean,
    metadata?: Record<string, unknown>
  ): Promise<AsyncGenerateResponse> {
    const payload: AsyncGenerateRequest = { count };
    if (generationSource) {
      payload.generation_source = generationSource;
    }

    // For product mode with server-side prompt generation, prompt may be empty
    if (prompt) {
      payload.prompt = prompt;
    }

    if (referenceImages && referenceImages.length > 0) {
      payload.reference_images = referenceImages;
    }

    // Add target dimensions if specified
    if (targetWidth && targetHeight) {
      payload.target_width = targetWidth;
      payload.target_height = targetHeight;
    }

    // Add input mode
    if (inputMode) {
      payload.input_mode = inputMode;
    }

    // Add model selection
    if (model) {
      payload.model = model;
    }

    // Add PRO mode flag
    if (proMode) {
      payload.pro_mode = true;
    }

    // Free-text jobs also retain their selected style for image history.
    if (generationParams) {
      payload.product_id = generationParams.product_id;
      payload.style_id = generationParams.style_id;
      payload.prompt_generation_params = generationParams.prompt_generation_params;
    }

    // Add metadata bag (e.g. carousel set id) only when provided
    if (metadata) {
      payload.metadata = metadata;
    }

    const response = await axiosInstance.post<AsyncGenerateResponse>(
      '/images/generate-async',
      payload
    );
    return response.data;
  }

  /**
   * Get list of user's generation jobs
   */
  async getJobs(status?: JobStatus, generationSource?: GenerationSource): Promise<ImageGenerationJob[]> {
    const { jobs } = await this.getJobsWithLimits(status, generationSource);
    return jobs;
  }

  /**
   * ジョブ一覧と同時実行枠をまとめて取得する。
   * 枚数セレクタを枠に合わせて絞るために使う。一覧のポーリングに相乗りするので追加リクエストは無い。
   */
  async getJobsWithLimits(
    status?: JobStatus,
    generationSource?: GenerationSource
  ): Promise<{ jobs: ImageGenerationJob[]; limits?: ConcurrencyLimits }> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    if (generationSource) params.generation_source = generationSource;
    const response = await axiosInstance.get<JobsListResponse>('/images/jobs', { params });
    return { jobs: response.data.jobs, limits: response.data.limits };
  }

  /**
   * Get job detail with generated images
   */
  async getJobDetail(jobId: string): Promise<JobDetailResponse> {
    const response = await axiosInstance.get<JobDetailResponse>(`/images/jobs/${jobId}`);

    // Fix image URLs for local development
    if (response.data.images) {
      response.data.images = response.data.images.map((img) => ({
        ...img,
        url: fixImageUrl(img.url),
      }));
    }

    return response.data;
  }

  /**
   * Cancel a pending job
   */
  async cancelJob(jobId: string): Promise<JobCancelResponse> {
    const response = await axiosInstance.delete<JobCancelResponse>(`/images/jobs/${jobId}`);
    return response.data;
  }

  /**
   * Delete a completed/failed job log
   */
  async deleteJobLog(jobId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axiosInstance.delete<{ success: boolean; message: string }>(`/images/jobs/${jobId}/log`);
      return response.data;
    } catch (error) {
      // 404 は「既に削除済み」（二重押下・古いポーリング結果での再表示）なので成功として扱う
      if ((error as { response?: { status?: number } })?.response?.status === 404) {
        return { success: true, message: 'ジョブは既に削除されています' };
      }
      throw error;
    }
  }

  // ========================================
  // Global Sample Methods
  // ========================================

  /**
   * Toggle global sample flag for an image
   * Only accessible by leader@example.com
   */
  async toggleGlobalSample(imageId: number): Promise<ToggleGlobalSampleResponse> {
    const response = await axiosInstance.put<ToggleGlobalSampleResponse>(
      `/images/${imageId}/global-sample`
    );
    return response.data;
  }

  // ========================================
  // Image Style Methods
  // ========================================

  /**
   * Get all active image styles
   * Transforms the grouped API response into a flat array
   */
  async getStyles(params?: { q?: string; tag?: string; subcategory_id?: number }): Promise<ImageStylesResponse> {
    const response = await axiosInstance.get<RawImageStylesResponse>('/images/styles', { params });

    // Transform grouped styles into a flat array with correct field names
    const flatStyles: ImageStyle[] = [];

    if (response.data.success && response.data.data?.grouped_styles) {
      for (const group of response.data.data.grouped_styles) {
        for (const style of group.styles) {
          flatStyles.push({
            id: style.id,
            name: style.name,
            description: style.description || '',
            category: group.category as 'sns' | 'marketing' | 'web' | 'other',
            tags: style.tags || [],
            subcategory_id: style.subcategory_id ?? null,
            subcategory_name: style.subcategory_name ?? null,
            subcategory_slug: style.subcategory_slug ?? null,
            platforms: style.platforms || [],
            aspect_ratio: '1:1', // Default aspect ratio (backend doesn't provide this)
            style_prompt: style.prompt_template || '',
            example_image_url: style.thumbnail_url || style.reference_image_url,
            is_active: true, // All returned styles are active
            is_favorited: style.is_favorited ?? false,
            my_rating: style.my_rating ?? null,
            my_rating_reason: style.my_rating_reason ?? null,
            ratings_summary: style.ratings_summary ?? { good: 0, bad: 0 },
            created_at: style.created_at || '',
            updated_at: '', // Not provided by grouped API
            cta: style.cta,
            feature: style.feature,
            min_material_count: style.min_material_count,
            max_material_count: style.max_material_count,
            background_tone: style.background_tone ?? null,
          });
        }
      }
    }

    // Re-sort: favorites first, then categories mixed newest-first
    flatStyles.sort(buildStyleBrowsingComparator(flatStyles));

    return {
      success: response.data.success,
      data: flatStyles,
    };
  }

  /**
   * Toggle favorite status for an image style
   */
  async toggleFavorite(styleId: number): Promise<{ is_favorited: boolean }> {
    const response = await axiosInstance.post<{ success: boolean; data: { is_favorited: boolean } }>(
      `/images/styles/${styleId}/favorite`
    );
    return response.data.data;
  }

  /**
   * Rate an image style as good or bad.
   */
  async rateStyle(
    styleId: number,
    rating: 'good' | 'bad',
    options?: { reason?: string; context?: Record<string, unknown> }
  ): Promise<{
    image_style_id: number;
    rating: 'good' | 'bad';
    reason: string | null;
    ratings_summary: { good: number; bad: number };
  }> {
    const response = await axiosInstance.post<{
      success: boolean;
      data: {
        image_style_id: number;
        rating: 'good' | 'bad';
        reason: string | null;
        ratings_summary: { good: number; bad: number };
      };
    }>(`/images/styles/${styleId}/rating`, {
      rating,
      reason: options?.reason,
      context: options?.context,
    });
    return response.data.data;
  }

  /**
   * Remove the current user's rating for an image style.
   */
  async deleteStyleRating(styleId: number): Promise<{
    image_style_id: number;
    rating: null;
    reason: null;
    ratings_summary: { good: number; bad: number };
  }> {
    const response = await axiosInstance.delete<{
      success: boolean;
      data: {
        image_style_id: number;
        rating: null;
        reason: null;
        ratings_summary: { good: number; bad: number };
      };
    }>(`/images/styles/${styleId}/rating`);
    return response.data.data;
  }

  /**
   * Get a specific image style by ID
   */
  async getStyle(id: number): Promise<ImageStyleResponse> {
    const response = await axiosInstance.get<ImageStyleResponse>(`/images/styles/${id}`);
    return response.data;
  }

  /**
   * Fetch only the full prompt_template for a single style.
   * The styles list omits prompt_template (P1-3) to keep the payload small,
   * so it is fetched on demand when a style is confirmed.
   */
  async getStylePrompt(id: number): Promise<string> {
    const response = await axiosInstance.get<{ success: boolean; data?: { prompt_template?: string } }>(
      `/images/styles/${id}`
    );
    return response.data?.data?.prompt_template ?? '';
  }

  /**
   * Get all active image style categories
   * For dynamic category filtering in StyleSelectModal
   */
  async getStyleCategories(): Promise<ImageStyleCategoryUser[]> {
    const response = await axiosInstance.get<ImageStyleCategoriesResponse>('/images/styles/categories');
    return response.data.data || [];
  }

  /**
   * Get recommended styles based on product or input text
   */
  async getStyleRecommendations(params?: { product_id?: number; text?: string; limit?: number }): Promise<StyleRecommendationsResponse> {
    const response = await axiosInstance.get<StyleRecommendationsResponse>('/images/styles/recommendations', { params });
    return response.data;
  }

  // ========================================
  // Sample Prompts Methods
  // ========================================

  /**
   * Get active sample prompts, optionally filtered by type
   * @param type - 'material' for material generation, 'banner' for ad generation
   */
  async getSamplePrompts(type?: SamplePromptType): Promise<SamplePrompt[]> {
    const params = type ? { type } : {};
    const response = await axiosInstance.get<SamplePromptsResponse>('/images/sample-prompts', { params });
    return response.data.data || [];
  }

  // ========================================
  // Prompt Generation Methods
  // ========================================

  /**
   * Generate an optimized image prompt from product information
   * Uses LLM (GPT-4o) to create structured prompts for image generation
   */
  async generatePrompt(request: GeneratePromptRequest): Promise<GeneratePromptResponse> {
    const response = await axiosInstance.post<GeneratePromptResponse>(
      '/images/generate-prompt',
      request
    );
    return response.data;
  }

  /**
   * Regenerate an image prompt with feedback/improvements
   * Uses the previous prompt and optional feedback to create an improved version
   */
  async regeneratePrompt(request: RegeneratePromptRequest): Promise<GeneratePromptResponse> {
    const response = await axiosInstance.post<GeneratePromptResponse>(
      '/images/regenerate-prompt',
      request
    );
    return response.data;
  }

  /**
   * Get available prompt options (target segments and appeal axes)
   */
  async getPromptOptions(): Promise<PromptOptionsResponse> {
    const response = await axiosInstance.get<PromptOptionsResponse>(
      '/images/prompt-options'
    );
    return response.data;
  }

  // ========================================
  // Magic Resize Methods
  // ========================================

  /**
   * Get magic resize presets grouped by category
   */
  async getMagicResizePresets(): Promise<MagicResizePresetsResponse> {
    const response = await axiosInstance.get<MagicResizePresetsResponse>(
      '/magic-resize/presets'
    );
    return response.data;
  }

  /**
   * Execute magic resize for an image with selected sizes
   */
  async executeMagicResize(
    imageId: number,
    sizes: Array<{ width: number; height: number }>,
    generationSource: Extract<GenerationSource, 'banner_ads' | 'image_generation'>,
    presetIds: number[] = []
  ): Promise<MagicResizeExecuteResponse> {
    const response = await axiosInstance.post<MagicResizeExecuteResponse>(
      `/images/${imageId}/magic-resize`,
      { sizes, preset_ids: presetIds, generation_source: generationSource }
    );
    return response.data;
  }

  // ========================================
  // Creative Analysis Methods
  // ========================================

  /**
   * Analyze a generated image for creative effectiveness
   * Returns scores for impression, visibility, comprehension, copy, and action
   */
  async analyzeCreative(imageId: number): Promise<CreativeAnalysis> {
    const response = await axiosInstance.post<{ success: boolean; data: { creative_analysis: CreativeAnalysis } }>(
      `/images/${imageId}/creative-analysis`
    );
    return response.data.data.creative_analysis;
  }
}

export default new ImageGenerationService();
